'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';

import InfiniteScrollList from '@/components/infinite-scroll-list';

import ForYouCardList from './for-you-card-list';
import ForYouCardSkeleton from './for-you-card-skeleton';
import StoryCardGridBoundary from './story-card-grid-boundary';
import StoryCardSkeleton from './story-card-skeleton';

import type { IDrama } from '@/apis/drama-mall';

const StoryCardGrid = dynamic(() => import('./story-card-grid'), {
    ssr: false,
    loading: () => <StoryCardSkeleton />,
});

interface IDiscoverContentProps {
    topPickList: IDrama[];
    mostPopularList: IDrama[];
    forYouList: IDrama[];
    hasMore: boolean;
    loadMore: () => Promise<void>;
}

export default function DiscoverContent(props: IDiscoverContentProps) {
    const { topPickList = [], mostPopularList = [], forYouList = [], hasMore, loadMore } = props;
    const t = useTranslations('Discover');

    return (
        <main className='relative z-10 px-[16px]'>
            <section>
                <div className='mb-[12px] mt-[8px] h-[24px] font-poppins-semibold text-[18px] leading-[24px] text-black'>
                    {t('discover_top_pick')} 🔥
                </div>
                {topPickList.length === 0 ? (
                    <StoryCardSkeleton />
                ) : (
                    <StoryCardGridBoundary>
                        <StoryCardGrid list={topPickList} module_name='top_pick' />
                    </StoryCardGridBoundary>
                )}
            </section>

            <section className='mt-[16px]'>
                <div className='mb-[12px] mt-[16px] h-[24px] font-poppins-semibold text-[18px] leading-[24px] text-black'>
                    {t('discover_most_popular')} 🚀
                </div>
                {mostPopularList.length === 0 ? (
                    <StoryCardSkeleton />
                ) : (
                    <StoryCardGridBoundary>
                        <StoryCardGrid list={mostPopularList} module_name='most_popular' />
                    </StoryCardGridBoundary>
                )}
            </section>

            <section className='mt-[16px]'>
                <div className='mb-[12px] mt-[16px] h-[24px] font-poppins-semibold text-[18px] leading-[24px] text-black'>
                    {t('discover_for_u')} ❤️‍🔥
                </div>
                {forYouList.length === 0 ? <ForYouCardSkeleton /> : <ForYouCardList list={forYouList} />}
            </section>

            {forYouList.length === 0 ? null : <InfiniteScrollList loadMore={loadMore} hasMore={hasMore} />}
        </main>
    );
}
